<template>
    <div class="card">
        <div class="flex justify-content-between">
            <h6>Search Criteria:</h6>
            <div>
                <Button :label="`Add New ${label('Task')}`" v-if="authStore.hasPermission(UserPermission.WRITE_TASK)" class="mb-2" @click="addTask" />
            </div>
        </div>

        <Accordion :activeIndex="0">
            <AccordionTab header="Use as few or as many as you wish, partial matches are acceptable.">
                <!-- All the criterias here -->
                <div class="p-fluid formgrid grid flex flex-wrap">
                    <div class="field col-12 sm:col-6 md:col-3">
                        <label class="mb-2" for="task_title">{{ label('Task') }} Title</label>
                        <autocomplete id="task_title" dropdown v-model="search.title" :suggestions="taskTitleSuggestions" @complete="searchTaskTitleSuggestions" />
                    </div>

                    <!--
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-text-input v-model="search.title" label="Task Title" placeholder="Enter task title.." />
                    </div>
                    -->
                    
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-multi-select v-model="search.progress" label="Status" :options="progressItems" optionLabel="name" optionValue="id" :squaredChips="true" :filter="false" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-dropdown v-model="search.priority" :options="priorityItems" optionLabel="name" optionValue="id" label="Priority" :filter="false" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="search.source" label="Source" :optionsTree="sourceVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="3" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="search.theme" label="Theme" :optionsTree="themeVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="3" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <UsersSelectionPanel v-model="search.assigned_to"> Assigned To: </UsersSelectionPanel>
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <custom-vue-tree-select :key="treeSelectKey" v-model="search.department_to" label="Department To" :optionsTree="departmentVueTree" :showLabel="true" :valueConsist="'ALL'" :multiple="true" :limit="3" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-selection-input v-model="search.with_learning_outcome" label="Learning Identified" :radio-type="true" :options="getCriteriaYesNoOptions()" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-calendar-input v-model="search.start_date_from" label="Start Date From" dateFormat="dd-mm-yy" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-calendar-input v-model="search.start_date_to" label="Start Date To" dateFormat="dd-mm-yy" :min-date="minStartDateTo" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-calendar-input v-model="search.due_date_from" label="Due Date From" dateFormat="dd-mm-yy" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-calendar-input v-model="search.due_date_to" label="Due Date To" dateFormat="dd-mm-yy" :min-date="minDueDateTo" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <base-selection-input v-model="search.overdue" :label="`Overdue ${label('Task')}`" :radio-type="true" :options="getOverdDueTasks()" />
                    </div>
                    <div class="field col-12 sm:col-6 md:col-3">
                        <label>Author:</label> {{author}}
                        <UsersPicker @user-selected="authorSelected" :modelValue="author"/>
                    </div>
                    <div v-if="isFeatureEnabled" class="field col-12 sm:col-6 md:col-3">
                        <base-dropdown v-model="search.assurance_level" :options="assuranceLevelOptions" optionLabel="percentage" optionValue="id" label="Assurance Level" :filter="false" />
                    </div>
                </div>
                <div class="grid col-6">
                    <div class="field col-6 md:col-3">
                        <Button type="button" label="Search" icon="pi pi-search" :loading="loading" @click="loadNonDraftTasks" />
                    </div>
                    <div class="field col-6 md:col-3">
                        <Button type="button" label="Clear" icon="pi pi-trash" @click="clearSearch" />
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </div>
</template>

<script setup>
import UsersSelectionPanel from '@/modules/user/components/UsersSelectionPanel';

import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

import { companyStore, departmentStore, sourceStore, taskStore, themeStore } from '../../../../../store/global-stores';
import { getAllAssuranceLevels, getAllPriorities, getAllProgress } from '../../../constants';
import { getCriteriaYesNoOptions } from '../../../../../constants';
import { getOverdDueTasks } from '../constants';
import { showToast } from '../../../../../utilities/toast-handler';
import { useAuthStore } from '@/modules/auth/store';
import { authApi } from '@/utilities/axios';
import hasOwn from 'core-js/internals/has-own-property';
import UsersPicker from '@/modules/user/components/UsersPicker.vue';
import { resetPagination } from '@/composables/PaginationComposable';

import { UserPermission } from '@/modules/permission/enum';
import { useLabels } from '@/composables/LabelComposable';
import { useAssuranceColors } from '@/composables/useAssuranceColors';

const { label } = useLabels();
const { assuranceLabels } = useAssuranceColors();
const { search, loading } = storeToRefs(taskStore);
const { sourceVueTree } = storeToRefs(sourceStore);
const { themeVueTree } = storeToRefs(themeStore);
const { departmentVueTree } = storeToRefs(departmentStore);

const router = useRouter();

const progressItems = ref(null);
const priorityItems = ref(null);
const minStartDateTo = ref(null);
const minDueDateTo = ref(null);
const treeSelectKey = ref(0);
const taskTitleSuggestions = ref([])

onMounted(() => {
    loadServices();
    loadOptions();
});

const authStore = useAuthStore();
const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

const author = ref(null)

function authorSelected(user){
    author.value = user.id

    search.value.author = [user.id];
}

const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');

const assuranceLevelOptions = computed(() =>
    getAllAssuranceLevels().map((level) => ({
        ...level,
        percentage: assuranceLabels.value[level.id] || level.percentage,
    }))
);

function searchTaskTitleSuggestions() {

    authApi.get('v2/tasks', {
        params: {
            company_id: company_id.value,
            page_size: 50,
            page: 1,
            sort: 'title asc',
            title: search.value.title,
            title_only: true,
        }
    }).then((response) => {
        taskTitleSuggestions.value = []

        if (response.statusCode === 200) {

            if (hasOwn(response, 'data')) {
                if (hasOwn(response, 'data')) {
                    if (hasOwn(response.data, 'data')) {
                        const tasks = response.data.data
                        //console.log('OK', tasks)

                        tasks.forEach(task => {
                            //console.log('task', task.title)
                            taskTitleSuggestions.value.push(task.title)
                        })
                    }
                }
            }
        }
    });
}

const loadServices = async () => {
    await sourceStore.fetchAllSources();
    await themeStore.fetchAllThemes();
    await departmentStore.fetchAllDepartments();
};

const loadOptions = () => {
    progressItems.value = getAllProgress();
    priorityItems.value = getAllPriorities();
};

const loadNonDraftTasks = async () => {
    resetPagination("taskList", 0, 10, [{field: "id", order: -1}])

    await taskStore.getExistingTasks('taskList');
};

const startDateFrom = computed(() => {
    return search.value.start_date_from;
});

const dueDateFrom = computed(() => {
    return search.value.due_date_from;
});

watch(startDateFrom, () => {
    if (startDateFrom.value) {
        // create criteria for start date to
        if (search.value.start_date_to < startDateFrom.value) {
            search.value.start_date_to = null;
        }
        const nextStartDay = new Date(startDateFrom.value);
        nextStartDay.setDate(nextStartDay.getDate() + 1);
        minStartDateTo.value = nextStartDay;
    } else {
        search.value.start_date_from = null;
        minStartDateTo.value = null;
    }
});

watch(dueDateFrom, () => {
    if (dueDateFrom.value) {
        // create criteria for due date to
        if (search.value.due_date_to < dueDateFrom.value) {
            search.value.due_date_to = null;
        }
        const nextDueDay = new Date(dueDateFrom.value);
        nextDueDay.setDate(nextDueDay.getDate() + 1);
        minDueDateTo.value = nextDueDay;
    } else {
        search.value.due_date_from = null;
        minDueDateTo.value = null;
    }
});

const clearSearch = () => {
    treeSelectKey.value++
    taskStore.initSearch();
    resetPagination("taskList", 0, 10, [{field: "id", order: -1}])
    author.value = null
    showToast({ severity: 'info', summary: 'Filters Cleared' }, 'All Criteria filters have been cleared');
    loadNonDraftTasks();
};

const addTask = () => {
    router.push({ name: 'TaskForm' });
};
</script>
